import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useAppSelector } from "../../../../../store/hooks";
import { selectProductDetail } from "../../../../../features/productAnalyzer/productAnalyzer.selector";
import { formatCurrency } from "../../../../../helpers";
import { colors } from "../../config";

interface PricingData {
  buyBoxPrice?: number | null;
  avgBBPrice?: number | null;
  minFBMPrice?: number | null;
  minFBAPrice?: number | null;
}

interface PanelProps {
  isOpen: boolean;
  anchorElement?: HTMLElement | null;
  text?: string | null;
  type?: "search" | "fulfilment" | "pricing";
  currentFulfilmentType?: "FBA" | "FBM";
  onFulfilmentSelect?: (type: "FBA" | "FBM") => void;
  pricingData?: PricingData;
  onPriceSelect?: (price: number) => void;
}

export default function Panel({
  isOpen,
  anchorElement,
  text,
  type = "search",
  currentFulfilmentType,
  onFulfilmentSelect,
  pricingData,
  onPriceSelect,
}: PanelProps) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const panelRef = useRef<HTMLDivElement>(null);
  const { productDetail } = useAppSelector(selectProductDetail);

  useEffect(() => {
    if (!isOpen || !anchorElement) return;

    const updatePosition = () => {
      const rect = anchorElement.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;

      const top = rect.bottom + scrollY + 8; // 8px margin
      let left = rect.left + scrollX;

      if (type === "search") {
        // For search, align to right edge
        left = rect.right + scrollX;
        if (panelRef.current) {
          left -= panelRef.current.offsetWidth;
        } else {
          // Fallback: estimate width
          left -= 200;
        }
      } else if (type === "pricing") {
        // For pricing, align middle of last column (Min FBA, $) with anchor center
        if (panelRef.current) {
          const panelWidth = panelRef.current.offsetWidth;
          const anchorCenter = rect.left + rect.width / 2;
          // Last column is approximately 1/5 of table width
          // Center of last column is at: panelWidth - (panelWidth/5)/2 = panelWidth - panelWidth/10
          const lastColumnCenterOffset = panelWidth - panelWidth / 10;
          left = anchorCenter + scrollX - lastColumnCenterOffset;
          // Adjust if panel would go off screen
          const viewportWidth = window.innerWidth;
          if (left + panelWidth > viewportWidth) {
            left = viewportWidth - panelWidth - 10;
          }
          if (left < scrollX) {
            left = scrollX + 10;
          }
        } else {
          // Fallback: align to left edge
          left = rect.left + scrollX;
        }
      } else {
        // For fulfilment, align to left edge
        // Adjust if panel would go off screen
        if (panelRef.current) {
          const panelWidth = panelRef.current.offsetWidth;
          const viewportWidth = window.innerWidth;
          if (left + panelWidth > viewportWidth) {
            left = viewportWidth - panelWidth - 10;
          }
        }
      }

      setPosition({ top, left });
    };

    // Initial position
    updatePosition();

    // Update after panel renders to get accurate width
    const timeoutId = setTimeout(updatePosition, 0);

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, anchorElement, type]);

  if (!isOpen) return null;

  const panelContent = (
    <Box
      ref={panelRef}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        position: "absolute",
        top: `${position.top}px`,
        left: `${position.left}px`,
        backgroundColor: colors.background.white,
        border: `1px solid ${colors.border.light}`,
        borderRadius: "4px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        zIndex: 1000,
        minWidth: "100px",
        overflow: "hidden",
      }}
    >
      {type === "search" && (
        <>
          <Box sx={{ padding: "0px 8px" }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: "12px",
                fontWeight: 400,
                color: colors.text.primary,
              }}
            >
              Search in:
            </Typography>
          </Box>
          <List sx={{ padding: 0 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (text) {
                    window.open(
                      `https://www.google.com/search?q=${encodeURIComponent(text)}`,
                      "_blank",
                    );
                  }
                }}
                disabled={!text}
                sx={{
                  padding: "0px 8px",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    "& .MuiTypography-root": {
                      color: "#EC6337",
                    },
                  },
                }}
              >
                <ListItemText
                  primary="Google"
                  primaryTypographyProps={{
                    fontSize: "12px",
                    color: colors.primary.main,
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (text) {
                    window.open(
                      `https://www.ebay.com/sch/i.html?_nkw=${text}`,
                      "_blank",
                    );
                  }
                }}
                disabled={!text}
                sx={{
                  padding: "0px 8px",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    "& .MuiTypography-root": {
                      color: "#EC6337",
                    },
                  },
                }}
              >
                <ListItemText
                  primary="eBay"
                  primaryTypographyProps={{
                    fontSize: "12px",
                    color: colors.primary.main,
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (text) {
                    window.open(
                      `https://walmart.com/search?q=${encodeURIComponent(text)}`,
                      "_blank",
                    );
                  }
                }}
                disabled={!text}
                sx={{
                  padding: "0px 8px",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    "& .MuiTypography-root": {
                      color: "#EC6337",
                    },
                  },
                }}
              >
                <ListItemText
                  primary="Walmart"
                  primaryTypographyProps={{
                    fontSize: "12px",
                    color: colors.primary.main,
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}
      {type === "fulfilment" && (
        <List sx={{ padding: 0 }}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={(e) => {
                e.stopPropagation();
                onFulfilmentSelect?.("FBA");
              }}
              selected={currentFulfilmentType === "FBA"}
              sx={{
                padding: "0px 8px",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  "& .MuiTypography-root": {
                    color: "#EC6337",
                  },
                },
                "&.Mui-selected": {
                  backgroundColor: colors.background.mediumGray,
                  "& .MuiTypography-root": {
                    color: colors.accent.orange,
                    fontWeight: 600,
                  },
                },
              }}
            >
              <ListItemText
                primary="FBA"
                primaryTypographyProps={{
                  fontSize: "12px",
                  color:
                    currentFulfilmentType === "FBA" ? colors.accent.orange : colors.primary.main,
                  fontWeight: currentFulfilmentType === "FBA" ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              onClick={(e) => {
                e.stopPropagation();
                onFulfilmentSelect?.("FBM");
              }}
              selected={currentFulfilmentType === "FBM"}
              sx={{
                padding: "0px 8px",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  "& .MuiTypography-root": {
                    color: "#EC6337",
                  },
                },
                "&.Mui-selected": {
                  backgroundColor: colors.background.mediumGray,
                  "& .MuiTypography-root": {
                    color: colors.accent.orange,
                    fontWeight: 600,
                  },
                },
              }}
            >
              <ListItemText
                primary="FBM"
                primaryTypographyProps={{
                  fontSize: "12px",
                  color:
                    currentFulfilmentType === "FBM" ? colors.accent.orange : colors.primary.main,
                  fontWeight: currentFulfilmentType === "FBM" ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>
        </List>
      )}
      {type === "pricing" && pricingData && (() => {
        const headerCellStyle = {
          fontSize: "11.5px",
          fontWeight: 400,
          paddingTop: 0,
          paddingLeft: "3px",
          paddingRight: "3px",
          paddingBottom: "2px",
          color: colors.text.secondary,
          textAlign: "center" as const,
        };

        const bodyCellStyle = {
          fontSize: "11.5px",
          paddingTop: "2px",
          paddingLeft: "3px",
          paddingRight: "3px",
          paddingBottom: "3px",
          color: colors.text.secondary,
          textAlign: "center" as const,
        };

        const priceCellStyle = {
          ...bodyCellStyle,
          color: colors.primary.main,
          cursor: "pointer" as const,
          "&:hover": {
            backgroundColor: colors.background.lightGray,
            color: colors.accent.orange,
          },
        };

        const calculatePrice = (basePrice: number | null | undefined, percent: number) => {
          return basePrice ? basePrice * (1 + percent / 100) : null;
        };

        const headerColumns = ["%", "BuyBox, $", "90d Avg BB, $", "Min FBM, $", "Min FBA, $"];
        const priceFields = [
          { key: "buyBoxPrice" as const, getter: () => pricingData.buyBoxPrice },
          { key: "avgBBPrice" as const, getter: () => pricingData.avgBBPrice },
          { key: "minFBMPrice" as const, getter: () => pricingData.minFBMPrice },
          { key: "minFBAPrice" as const, getter: () => pricingData.minFBAPrice },
        ];

        return (
          <Box sx={{ paddingY: "5px", paddingX: "8px", minWidth: "450px" }} data-pricing-panel>
            <Table 
              size="small" 
              sx={{ 
                borderCollapse: "separate",
                borderSpacing: 0,
                border: "none",
                borderRadius: "6px",
                overflow: "hidden",
                "& .MuiTableCell-root": { 
                  borderRight: `1px solid ${colors.border.medium}`,
                  borderTop: "none",
                  borderLeft: "none",
                  borderBottom: "none",
                  "&:last-of-type": {
                    borderRight: "none",
                  },
                },
                "& .MuiTableHead .MuiTableRow:first-of-type .MuiTableCell-root": {
                  borderBottom: "none",
                  borderTop: "none",
                },
                "& .MuiTableBody .MuiTableRow .MuiTableCell-root": {
                  borderBottom: "none !important",
                },
                "& .MuiTableRow-root": {
                  borderBottom: "none",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {headerColumns.map((label) => (
                    <TableCell key={label} sx={headerCellStyle}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell 
                    colSpan={5} 
                    sx={{ 
                      borderBottom: `1px solid ${colors.border.medium} !important`, 
                      borderTop: "none !important",
                      borderRight: "none !important",
                      borderLeft: "none !important",
                      padding: 0, 
                      height: "1px",
                      lineHeight: "1px",
                    }} 
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {[0, 2, 5, 10].map((percent) => (
                  <TableRow key={percent}>
                    <TableCell sx={bodyCellStyle}>
                      {percent === 0 ? "0" : `+${percent}%`}
                    </TableCell>
                    {priceFields.map(({ key, getter }) => {
                      const price = calculatePrice(getter(), percent);
                      return (
                        <TableCell
                          key={key}
                          onClick={() => price && onPriceSelect?.(price)}
                          sx={{
                            ...priceCellStyle,
                            cursor: price ? "pointer" : "default",
                            "&:hover": price ? priceCellStyle["&:hover"] : {},
                          }}
                        >
                          {price ? Number(price).toFixed(2) : "-"}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        );
      })()}
    </Box>
  );

  return createPortal(panelContent, document.body);
}
